import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MessageSquare, ArrowUpRight, ChevronDown } from "lucide-react";
import api from "../lib/api";

export function CommunicationCard({ resource, recordId, recordName }) {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ all: 0 });
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(() => {
    api.get(`/comm/history/${resource}/${recordId}`)
      .then((r) => setCounts(r.data.counts || { all: 0 }))
      .catch(() => {});
  }, [resource, recordId]);

  useEffect(() => { load(); }, [load]);

  const go = (mode) => navigate(`/communication/${resource}/${recordId}?mode=${mode}`);

  const actions = [
    { mode: "call", label: "Call", Icon: Phone },
    { mode: "email", label: "Email", Icon: Mail },
    { mode: "sms", label: "SMS", Icon: MessageSquare },
  ];

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="communication-card">
      <div className={`flex items-center justify-between ${collapsed ? "" : "mb-4"}`}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-left"
          title={collapsed ? "Expand" : "Collapse"}
          data-testid="comm-collapse-btn"
        >
          <Phone className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Communication</h3>
          {counts.all > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground" data-testid="comm-count-badge">{counts.all}</span>
          )}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
        <button
          onClick={() => go("history")}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="comm-view-history-btn"
        >
          History <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {actions.map(({ mode, label, Icon }) => (
              <button
                key={mode}
                onClick={() => go(mode)}
                data-testid={`comm-${mode}-btn`}
                className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-border bg-secondary/40 py-3 text-xs font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {counts.all === 0 && (
            <p className="text-xs text-muted-foreground mt-3 text-center" data-testid="comm-empty-hint">
              No communications yet. Start a call, email or text.
            </p>
          )}
        </>
      )}
    </div>
  );
}
